/**
 * Pure, DI-free parsing helpers for splitting company-register data
 * (a single name string, a single address string) into the multi-field
 * shape `Person`/`Clients` actually store. See
 * docs/PROMPT_company-register-apply-refinements.md (AG-23d) for the
 * design and the real captured sample these are built against.
 */

/**
 * Register data lists a director as "Прізвище Ім'я По батькові" — the same
 * surname-first order this codebase already displays Person names in (see
 * `edit-person.component.html`'s title: `PERSONLNAME PERSONFNAME
 * PERSONMNAME`), confirmed live against opendatabot (regcode 41686923,
 * 2026-08-27: "Петреченко Ангеліна Олександрівна") — so a plain whitespace
 * split lines up with existing fields rather than guessing at a different
 * order.
 */
export function parseRegisterName(fullName: string): { lname: string | null; fname: string | null; mname: string | null } {
  const parts = fullName.trim().split(/\s+/).filter(p => p.length > 0);
  return {
    lname: parts[0] || null,
    fname: parts[1] || null,
    mname: parts.slice(2).join(' ') || null,
  };
}

export interface ParsedRegisterAddress {
  country: string | null;
  zip: string | null;
  city: string | null;
  addr1: string | null;
  addr2: string | null;
}

const CITY_PREFIXES = ['місто ', 'село ', 'смт ', 'селище '];
const ADMIN_DIVISION_SEGMENT = /обл\.?$|область$|р-н$|район$/i;

/**
 * Parses the comma-delimited shape confirmed live against real captured
 * samples: "Країна, ZIP, [adm. division segments, ]Місто, Вулиця, будинок
 * N[, офіс/корпус M]". Returns null — never a guessed partial split — when
 * the string doesn't match this shape, so an unrecognized format falls back
 * to the pre-AG-23d behavior (the whole string into CLIENTADDR1) rather
 * than misplace text into the wrong field.
 *
 * The administrative-division segments right after the postal code vary by
 * how the settlement is classified, confirmed against three real samples:
 * opendatabot's Kyiv (regcode 41686923) has none — Kyiv has its own special
 * city status, not part of any oblast/raion; youcontrol's Lviv (regcode
 * 37655682, 2026-08-28: "...79040, Львівська обл., місто Львів, ...") has
 * one (oblast only — a regional-center city doesn't belong to a raion
 * within it); youcontrol's Navariya village (regcode 30763942, 2026-08-28:
 * "...81105, Львівська обл., Львівський р-н, село Наварія, ...") has two
 * (oblast *and* raion — a village belongs to both). Skipping however many
 * are present is a loop over a plain positional check (does this segment
 * end in "обл."/"область"/"р-н"/"район"), not a guess about which
 * settlements have which divisions above them.
 */
export function parseRegisterAddress(raw: string): ParsedRegisterAddress | null {
  const parts = raw.split(',').map(p => p.trim()).filter(p => p.length > 0);
  if (parts.length < 4 || !/^\d{5}$/.test(parts[1])) { return null; }

  const country = parts[0];
  const zip = parts[1];

  let cityIndex = 2;
  while (cityIndex < parts.length && ADMIN_DIVISION_SEGMENT.test(parts[cityIndex])) { cityIndex++; }

  const cityPart = parts[cityIndex];
  if (!cityPart) { return null; }
  const matchedPrefix = CITY_PREFIXES.find(prefix => cityPart.toLowerCase().startsWith(prefix));
  if (!matchedPrefix) { return null; }   // no recognized city marker -- don't guess the rest of the layout
  const city = cityPart.slice(matchedPrefix.length).trim();

  const remaining = parts.slice(cityIndex + 1);
  if (remaining.length === 0) { return null; }

  const addr1 = remaining.slice(0, 2).join(', ') || null;   // street [+ building]
  const addr2 = remaining.slice(2).join(', ') || null;       // офіс/корпус/etc., if present

  return { country, zip, city, addr1, addr2 };
}

/** Case/whitespace-insensitive equality — the only comparison this feature
 *  family uses for matching, deliberately not a similarity/edit-distance
 *  score (see AG-23d's "What NOT to do"). */
export function normalizeForMatch(s: string | null | undefined): string {
  return (s || '').trim().toLowerCase();
}
